import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BookOpen, Play, TrendingUp, TrendingDown, Minus, Target, CheckCircle, BarChart2, Lightbulb, ChevronDown, ChevronRight, Star } from 'lucide-react';
import { ModuleData, ModuleProgress } from '../../types';
import { Button } from '../ui/Button';
import { ProgressRing, ProgressBar } from '../ui/Progress';
import * as Icons from 'lucide-react';
import { useMediaQuery } from 'react-responsive';
import { GroupId } from '../../config/systemConfig';
import { useAppStore } from '../../hooks/useAppStore';
import { computeTierProgress } from '../../utils/adaptiveEngine';
import { formatSkill, formatSubSkill } from '../../utils/formatters';
import { getModuleDisplayInfo } from '../../config/moduleContent';
import { computeSkillMasteries } from '../../utils/skillAggregation';
import { useFocusTrap } from '../../hooks/useFocusTrap';
interface ModulePanelProps {
  isOpen: boolean;
  onClose: () => void;
  module: ModuleData | null;
  progress: ModuleProgress | null;
  onStartPractice: (moduleId: string) => void;
  onViewExplanation: (moduleId: string) => void;
  selectedGroup: GroupId;
}
export function ModulePanelRedesigned({
  isOpen,
  onClose,
  module,
  progress,
  onStartPractice,
  onViewExplanation,
  selectedGroup
}: ModulePanelProps) {
  const isMobile = useMediaQuery({
    maxWidth: 768
  });
  const focusTrapRef = useFocusTrap(isOpen);
  const {
    state,
    getStartingLevel,
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // Get dynamic content
  const displayInfo = module ? getModuleDisplayInfo(module.id, selectedGroup) : null;
  const title = displayInfo?.title || module?.title;
  const description = displayInfo?.description || module?.description;
  // v10.0: Use group-scoped mastery
  const masteryPercentage = module ? getModuleMasteryForGroup(module.id) : 0;
  // v10.0: Use group-scoped answered questions count
  const answeredCount = module ? getAnsweredQuestions(module.id).length : 0;
  // Compute skill-level aggregated masteries
  const skillMasteries = useMemo(() => {
    if (!module) return [];
    return computeSkillMasteries(module.id, selectedGroup, state.questionStates);
  }, [module, selectedGroup, state.questionStates]);
  if (!module || !progress) return null;
  const IconComponent = (Icons as any)[module.icon] || Icons.HelpCircle;
  const bgColorMap: Record<string, string> = {
    'text-blue-500': 'bg-blue-50',
    'text-indigo-500': 'bg-indigo-50',
    'text-emerald-500': 'bg-emerald-50',
    'text-rose-500': 'bg-rose-50',
    'text-amber-500': 'bg-amber-50',
    'text-purple-500': 'bg-purple-50',
    'text-teal-500': 'bg-teal-50',
    'text-cyan-500': 'bg-cyan-50'
  };
  const iconBgColor = bgColorMap[module.color] || 'bg-slate-50';
  // Compute Tier Progress for "Current Level" display
  const startingTier = getStartingLevel(module.id, selectedGroup) || 1;
  const tierProgressInfo = computeTierProgress(module.id, selectedGroup, state.questionStates, startingTier);
  const currentTier = tierProgressInfo.tierDetails.find(t => t.isCurrent) || tierProgressInfo.tierDetails[0];
  // Sort skills: Struggling first, then by lowest mastery
  const sortedSkills = [...skillMasteries].sort((a, b) => {
    if (a.trend === 'struggling' && b.trend !== 'struggling') return -1;
    if (b.trend === 'struggling' && a.trend !== 'struggling') return 1;
    return a.masteryScore - b.masteryScore;
  });
  // Find struggling skill for Smart Tip
  const strugglingSkill = sortedSkills.find(s => s.trend === 'struggling' || s.masteryScore < 35);
  return <AnimatePresence data-id="element-3851">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-40" data-id="element-3852" />

          {/* Panel */}
          <motion.div ref={focusTrapRef} role="dialog" aria-modal="true" aria-labelledby="module-panel-title" initial={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} animate={isMobile ? {
        y: 0
      } : {
        x: 0
      }} exit={isMobile ? {
        y: '100%'
      } : {
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className={`
              fixed z-50 bg-[#FAFAF8] shadow-2xl flex flex-col
              ${isMobile ? 'inset-x-0 bottom-0 h-[90vh] rounded-t-[2.5rem]' : 'inset-y-0 right-0 w-full max-w-xl border-l border-white/50'}
            `} data-id="element-3853">
            {/* Mobile Drag Handle */}
            {isMobile && <div className="w-full flex justify-center pt-4 pb-2" onClick={onClose} data-id="element-3854">
                <div className="w-16 h-1.5 bg-slate-300/50 rounded-full" data-id="element-3855" />
              </div>}

            {/* Header */}
            <div className="p-8 pb-6 border-b border-slate-200/60 flex items-start justify-between bg-white/50 backdrop-blur-sm" data-id="element-3856">
              <div className="flex items-start gap-5 pr-8" data-id="element-3857">
                <div className={`p-4 rounded-2xl ${iconBgColor} shadow-sm border border-white`} data-id="element-3858">
                  <IconComponent className={`w-8 h-8 ${module.color}`} strokeWidth={2.5} data-id="element-3859" />
                </div>
                <div data-id="element-3860">
                  <h2 id="module-panel-title" className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-2" data-id="element-3861">
                    {title}
                  </h2>
                  <p className="text-slate-500 font-medium text-base leading-relaxed" data-id="element-3862">
                    {description}
                  </p>
                </div>
              </div>
              <button onClick={onClose} className="p-2.5 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600 transition-colors" aria-label="Paneel sluiten" data-id="element-3863">
                <X className="w-6 h-6" data-id="element-3864" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-8 flex flex-col scroll-smooth" data-id="element-3865">
              <div className="flex flex-col h-full space-y-8" data-id="element-3866">
                {/* 1. Quick Stats Row */}
                <div className="grid grid-cols-3 gap-4" data-id="element-3867">
                  {/* Current Level */}
                  <div className="bg-white rounded-3xl p-4 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-3868">
                    <div className="mb-3 bg-indigo-50 p-2.5 rounded-xl text-indigo-600" data-id="element-3869">
                      <Target className="w-6 h-6" data-id="element-3870" />
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-3871">
                      {currentTier?.label || '-'}
                    </div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1" data-id="element-3872">
                      Niveau
                    </div>
                  </div>

                  {/* Mastery */}
                  <div className="bg-white rounded-3xl p-4 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-3873">
                    <div className="mb-2" data-id="element-3874">
                      <ProgressRing progress={masteryPercentage} size={48} strokeWidth={5} showLabel={false} color={masteryPercentage >= 95 ? 'text-emerald-500' : 'text-indigo-600'} data-id="element-3875" />
                    </div>
                    <div className="text-lg font-black text-slate-900" data-id="element-3876">
                      {Math.round(masteryPercentage)}%
                    </div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider" data-id="element-3877">
                      Beheersing
                    </div>
                  </div>

                  {/* Questions Answered - v10.0: Now group-scoped */}
                  <div className="bg-white rounded-3xl p-4 border border-slate-100 shadow-sm flex flex-col items-center justify-center text-center" data-id="element-3878">
                    <div className="mb-3 bg-emerald-50 p-2.5 rounded-xl text-emerald-600" data-id="element-3879">
                      <CheckCircle className="w-6 h-6" data-id="element-3880" />
                    </div>
                    <div className="text-2xl font-black text-slate-900" data-id="element-3881">
                      {answeredCount}
                    </div>
                    <div className="text-xs text-slate-400 font-bold uppercase tracking-wider mt-1" data-id="element-3882">
                      Gemaakt
                    </div>
                  </div>
                </div>

                {/* 2. Skill Mastery Section */}
                <div data-id="element-3883">
                  <div className="flex items-center gap-3 mb-5" data-id="element-3884">
                    <div className="bg-slate-100 p-2 rounded-lg" data-id="element-3885">
                      <BarChart2 className="w-5 h-5 text-slate-500" data-id="element-3886" />
                    </div>
                    <h3 className="text-lg font-black text-slate-800" data-id="element-3887">
                      Jouw Vaardigheden
                    </h3>
                  </div>

                  {sortedSkills.length > 0 ? <div className="space-y-4" data-id="element-3888">
                      {sortedSkills.map(skill => {
                  const isMastered = skill.masteryScore >= 85;
                  const isGood = skill.masteryScore >= 50;
                  const isExpanded = expandedSkill === skill.skill;
                  const hasMultipleSubSkills = skill.subSkills.length > 1;
                  let colorClass = 'bg-amber-500';
                  let bgClass = 'bg-white border-amber-100 shadow-amber-100/50';
                  let textClass = 'text-amber-700';
                  let iconBg = 'bg-amber-50';
                  if (isMastered) {
                    colorClass = 'bg-emerald-500';
                    bgClass = 'bg-white border-emerald-100 shadow-emerald-100/50';
                    textClass = 'text-emerald-700';
                    iconBg = 'bg-emerald-50';
                  } else if (isGood) {
                    colorClass = 'bg-indigo-500';
                    bgClass = 'bg-white border-indigo-100 shadow-indigo-100/50';
                    textClass = 'text-indigo-700';
                    iconBg = 'bg-indigo-50';
                  }
                  return <div key={skill.skill} className={`rounded-2xl border-2 ${bgClass} shadow-sm overflow-hidden transition-all`} data-id="element-3889">
                            {/* Skill Row */}
                            <div className={`p-4 flex items-center justify-between gap-4 ${hasMultipleSubSkills ? 'cursor-pointer hover:bg-slate-50/50' : ''}`} onClick={() => {
                      if (hasMultipleSubSkills) {
                        setExpandedSkill(isExpanded ? null : skill.skill);
                      }
                    }} data-id="element-3890">
                              <div className="flex-1" data-id="element-3891">
                                <div className="flex items-center justify-between mb-2" data-id="element-3892">
                                  <div className="flex items-center gap-3" data-id="element-3893">
                                    {hasMultipleSubSkills && <div className={`p-1 rounded-md ${iconBg}`} data-id="element-3894">
                                        {isExpanded ? <ChevronDown className={`w-4 h-4 ${textClass}`} data-id="element-3895" /> : <ChevronRight className={`w-4 h-4 ${textClass}`} data-id="element-3896" />}
                                      </div>}
                                    <span className={`font-bold text-base capitalize ${textClass}`} data-id="element-3897">
                                      {formatSkill(skill.skill)}
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-2" data-id="element-3898">
                                    {skill.trend === 'improving' && <TrendingUp className="w-4 h-4 text-emerald-500" data-id="element-3899" />}
                                    {skill.trend === 'struggling' && <TrendingDown className="w-4 h-4 text-amber-500" data-id="element-3900" />}
                                    {skill.trend === 'stable' && <Minus className="w-4 h-4 text-slate-400" data-id="element-3901" />}
                                    <span className={`text-sm font-black ${textClass} bg-slate-50 px-2 py-0.5 rounded-md`} data-id="element-3902">
                                      {skill.masteryScore}%
                                    </span>
                                  </div>
                                </div>
                                <ProgressBar value={skill.masteryScore} height={6} color={colorClass} className="bg-slate-100" data-id="element-3903" />
                              </div>
                            </div>

                            {/* SubSkill Drill-Down */}
                            <AnimatePresence data-id="element-3904">
                              {isExpanded && hasMultipleSubSkills && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2
                      }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-3905">
                                  <div className="p-3 space-y-2" data-id="element-3906">
                                    {skill.subSkills.sort((a, b) => a.masteryScore - b.masteryScore).map(sub => <div key={sub.subSkill} className="flex items-center justify-between bg-white rounded-xl px-3 py-2 border border-slate-100" data-id="element-3907">
                                          <span className="text-sm font-medium text-slate-600 capitalize" data-id="element-3908">
                                            {formatSubSkill(sub.subSkill)}
                                          </span>
                                          <span className="text-xs font-bold text-slate-400 bg-slate-50 px-2 py-1 rounded-md" data-id="element-3909">
                                            {sub.masteryScore}%
                                          </span>
                                        </div>)}
                                  </div>
                                </motion.div>}
                            </AnimatePresence>
                          </div>;
                })}
                    </div> : <div className="text-center py-10 bg-white rounded-3xl border-2 border-dashed border-slate-200" data-id="element-3910">
                      <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-3911">
                        <BarChart2 className="w-6 h-6 text-slate-300" data-id="element-3912" />
                      </div>
                      <p className="text-slate-500 font-medium" data-id="element-3913">
                        Start met oefenen om je vaardigheden te zien!
                      </p>
                    </div>}
                </div>

                {/* 3. Smart Tip */}
                {strugglingSkill && <div className="bg-amber-50 border-2 border-amber-100 rounded-3xl p-5 flex gap-4 shadow-sm" data-id="element-3914">
                    <div className="bg-white p-3 rounded-2xl h-fit shadow-sm text-amber-500 border border-amber-100" data-id="element-3915">
                      <Lightbulb className="w-6 h-6" data-id="element-3916" />
                    </div>
                    <div data-id="element-3917">
                      <h4 className="font-black text-amber-800 text-base mb-1" data-id="element-3918">
                        Focus Tip
                      </h4>
                      <p className="text-sm text-amber-700 font-medium leading-relaxed" data-id="element-3919">
                        <span className="font-bold capitalize underline decoration-amber-300 decoration-2 underline-offset-2" data-id="element-3920">
                          {formatSkill(strugglingSkill.skill)}
                        </span>{' '}
                        kan nog wat extra aandacht gebruiken. Het systeem past
                        zich automatisch aan om je hierbij te helpen!
                      </p>
                    </div>
                  </div>}

                {/* Sticky Bottom Actions */}
                <div className="mt-auto space-y-4 pt-6 border-t border-slate-200/60" data-id="element-3921">
                  <Button onClick={() => onStartPractice(module.id)} size="xl" fullWidth className="shadow-xl shadow-indigo-200 h-16 text-lg group" data-id="element-3922">
                    <span className="font-black mr-2" data-id="element-3923">Start Oefensessie</span>
                    <div className="bg-white/20 rounded-lg p-1 group-hover:bg-white/30 transition-colors" data-id="element-3924">
                      <Play className="w-5 h-5 fill-current" data-id="element-3925" />
                    </div>
                  </Button>

                  <Button onClick={() => onViewExplanation(module.id)} variant="ghost" size="lg" fullWidth className="text-slate-400 hover:text-slate-600 hover:bg-slate-100" data-id="element-3926">
                    <BookOpen className="w-5 h-5 mr-2" data-id="element-3927" />
                    Bekijk Uitleg
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}